import { ENDPOINTS } from "@/constants/api/endpoints";
import { DEFAULT_RATING_THRESHOLDS } from "@/constants/api/search-params";
import { fetchQuery } from "../server-query-utils";
import type {
  NormalizedFilterOption,
  NormalizedSearchFilters,
  SearchFilterOption,
  SearchFiltersResponse,
} from "@/types/search-filters";

/**
 * `/search/filters` is new and has no published example response, so we read it
 * tolerantly: any of `slug`/`id` may carry the value and any of
 * `title`/`label`/`name` may carry the display text. Anything we can't read is
 * dropped rather than rendered as `undefined`.
 */
const toOption = (
  option: SearchFilterOption
): NormalizedFilterOption | null => {
  const id = option.slug ?? (option.id != null ? String(option.id) : undefined);
  const label = option.title ?? option.label ?? option.name;

  if (!id || !label) return null;

  return { id: String(id), label, count: option.count ?? 0 };
};

/** Flattens one level of nesting (amenity -> subType/subAmenities). */
export const normalizeOptions = (
  options: SearchFilterOption[] | undefined
): NormalizedFilterOption[] => {
  if (!Array.isArray(options)) return [];

  const result: NormalizedFilterOption[] = [];

  for (const option of options) {
    const children = option.subType ?? option.subAmenities;

    if (Array.isArray(children) && children.length > 0) {
      for (const child of children) {
        if (child.inFilter === false) continue;
        const normalizedChild = toOption(child);
        if (normalizedChild) result.push(normalizedChild);
      }
      continue;
    }

    if (option.inFilter === false) continue;
    const normalized = toOption(option);
    if (normalized) result.push(normalized);
  }

  // De-duplicate by id — parents and children can collide on slug
  return Array.from(
    new Map(result.map((option) => [option.id, option])).values()
  );
};

const normalizeThresholds = (
  thresholds: SearchFiltersResponse["ratingThresholds"]
): number[] => {
  if (!Array.isArray(thresholds) || thresholds.length === 0) {
    return DEFAULT_RATING_THRESHOLDS;
  }

  const values = thresholds
    .map((threshold) =>
      typeof threshold === "number" ? threshold : Number(threshold?.id)
    )
    .filter((value) => Number.isFinite(value) && value > 0);

  return values.length > 0 ? values : DEFAULT_RATING_THRESHOLDS;
};

export const normalizeSearchFilters = (
  data: SearchFiltersResponse | null
): NormalizedSearchFilters => ({
  amenities: normalizeOptions(data?.amenities),
  types: normalizeOptions(data?.types),
  origins: normalizeOptions(data?.origins),
  ratingThresholds: normalizeThresholds(data?.ratingThresholds),
  priceMin: data?.price?.min ?? null,
  priceMax: data?.price?.max ?? null,
  capacityMax: data?.capacity?.max ?? null,
});

/** Server-side fetch of the aggregated PLP filter options. */
export async function searchFiltersQuery(): Promise<NormalizedSearchFilters> {
  const result = await fetchQuery<SearchFiltersResponse>(
    ENDPOINTS.ACCOMMODATIONS.SEARCH_FILTERS,
    { cache: "force-cache", revalidate: 1800 }
  );

  return normalizeSearchFilters(result.data);
}
